import {FC, useState} from "react";
import {
    BonusCodeAccess,
    BonusCodeType,
    ConsumerBonusCode,
    ICodeTypeBalance,
    ICodeTypeItem
} from "@interfaces/consumer/bonusCodes";
import {BonusCodeFormValues} from "@components/consumer/bonuses/forms";
import ConsumerBonusService from "@api/consumer/consumerBonusService";
import {toastr} from "@/utils/toastHelper";
import BonusCodeForm from "@components/consumer/bonuses/bonusCodeForm";
import {useNavigate} from "react-router";
import withPage from "@components/hoc/withPage";
import {ObjectSchema} from "yup";
import * as Yup from "yup";
import CreatedBonusCodesListModal from "@components/consumer/bonuses/createdBonusCodesListModal";

const AddBonusCodeForm: FC = () => {

    const navigate = useNavigate();
    const [isListModalOpened, setListModalOpened] = useState(false);
    const [createdCodes, setCreatedCodes] = useState<ConsumerBonusCode[]>([]);
    const onSuccess = (codes: ConsumerBonusCode[]) => {
        setCreatedCodes(codes);
        setListModalOpened(true);
    }

    const closeListModal = () => {
        setListModalOpened(false);
        setCreatedCodes([]);
        navigate(`/consumer/bonuses/`, { replace: true });
    }

    const initial: BonusCodeFormValues = {
        code: '',
        count: 1,
        maxUseCount: 1,
        type: BonusCodeType.Balance,
        itemId: '',
        itemCount: 1,
        money: 100,
        access: BonusCodeAccess.Default,
        timeLimited: false,
        isActive: false
    };

    const onSubmit = async (values: BonusCodeFormValues) => {

        const api = new ConsumerBonusService();

        const value: ICodeTypeBalance | ICodeTypeItem = values.type === BonusCodeType.Balance ? {
            value: values.money!
        } : {
            itemId: values.itemId!,
            count: values.itemCount!
        }

        const result = await api.putConsumerBonusCodes({
            code: values.code,
            count: values.code ? 1 : values.count ?? 1,
            maxUseCount: values.maxUseCount,
            type: values.type,
            value: JSON.stringify(value),
            access: values.access,
            beginTime: values.timeLimited ? values.beginTime?.toUTCString() : undefined,
            endTime: values.timeLimited ? values.endTime?.toUTCString() : undefined
        });

        if (result.success) {
            toastr.success('Создание бонусных кодов', 'Коды успешно созданы!');
            if(result.result!.length === 1) {
                navigate(`/consumer/bonuses/${result.result![0].id}`, { replace: true });
            }
            else {
                onSuccess(result.result!);
            }
        }
        else {
            if (result.status === 404) {
                toastr.error('Ошибка создания кодов', 'Некорректные данные. Обновите страницу и попробуйте снова.');
            }
            if (result.errorCode === 'InvalidModel') {
                toastr.error('Ошибка создания кодов', 'Некорректные данные. Обновите страницу и попробуйте снова.');
            }
        }
    }

    const validationSchema: ObjectSchema<BonusCodeFormValues> = Yup.object({
        code: Yup.string()
            .max(36)
            .when('count', {
                is: (count: number) => count > 1,
                then: schema => schema.equals([''], 'Нельзя указать код при создании нескольких кодов'),
                otherwise: schema => schema.min(3, 'Минимальная длина кода 3 символа')
            }),
        count: Yup.number(),
        maxUseCount: Yup.number()
            .min(0)
            .max(10000)
            .required(),

        timeLimited: Yup.boolean().required(),
        beginTime: Yup.date(),
        endTime: Yup.date(),

        isActive: Yup.boolean().required(),
        access: Yup.mixed<BonusCodeAccess>()
            .oneOf(Object.values(BonusCodeAccess))
            .required(),

        type: Yup.mixed<BonusCodeType>()
            .oneOf(Object.values(BonusCodeType))
            .required(),
        itemId: Yup.string()
            .when("type", {
                is: BonusCodeType.Item,
                then: schema => schema.required()
            }),
        itemCount: Yup.number()
            .when("type", {
                is: BonusCodeType.Item,
                then: schema => schema
                    .min(1)
                    .max(2000000000)
                    .required()
            }),
        money: Yup.number()
            .when("type", {
                is: BonusCodeType.Balance,
                then: schema => schema
                    .min(1)
                    .max(100000)
                    .required()
            })
    })

    return (
        <>
            <BonusCodeForm
                initial={initial}
                onSubmit={onSubmit}
                validationSchema={validationSchema}
            />
            <CreatedBonusCodesListModal
                isOpened={isListModalOpened}
                toggle={closeListModal}
                codes={createdCodes}
            />
        </>
    )
}

export default withPage(AddBonusCodeForm);